/**
 * Storefront interaction events for GA4 ONLY (phase 1 of the GA4 event plan).
 *
 * ANALYTICS ONLY, BY CONSTRUCTION. Every event here is sent with send_to set
 * to the GA4 measurement id alone, never to Google Ads. The five events that
 * also reach Google Ads (view_item, view_item_list, view_search_results,
 * add_to_cart, begin_checkout) live in assets/google-events.js and are not
 * touched: this file never sends those names for the same moments, and never
 * marks anything as a conversion.
 *
 * META AND CHECKOUT UNTOUCHED. The theme's own events (theme:product:add,
 * theme:cart:add, ...) feed the Meta pixel and the n8n webhooks. This file only
 * LISTENS to them and never dispatches one, and it changes nothing about any
 * click: every listener is passive, never calls preventDefault or
 * stopPropagation, and reads the page without writing to it.
 *
 * PRIVACY. No email, phone, name, address, postcode, note text or WhatsApp
 * message is ever sent. contactDetailsCollected carries the shopper's email and
 * phone; only the fact that it happened (and the ISO country) is used. Search
 * terms that look like an email or a long number are replaced.
 *
 * Events (see the GA4 event plan for the full catalogue):
 *   nav_click, search, search_no_results, search_suggestion_click,
 *   view_item_list (rails, and products loaded by scrolling or filtering; GA4
 *   only, distinct from the collection-page list in google-events.js),
 *   select_item, filter_apply, filter_remove, price_filter_apply, sort_apply,
 *   select_promotion, select_variant, colour_sibling_click, plating_select,
 *   size_guide_open, delivery_estimate_check, add_to_cart_error,
 *   buy_now_click, made_to_order_click, made_to_order_submit, view_cart,
 *   remove_from_cart, cart_quantity_increase, contact_details_open,
 *   add_contact_info, contact_details_abandon, whatsapp_click, cookie_consent.
 *
 * Phases 2 and 3 (same rules): scroll_depth, article_read, page_not_found,
 * menu_open, submenu_open, logo_click, account_click, social_click,
 * announcement_click, search_open, search_clear, filter_panel_open,
 * filter_clear_all, filter_show_more, colour_swatch_hover,
 * story_highlight_click, category_tile_click, view_all_click, quick_add_open,
 * view_promotion, offer_toast_close, media_view, media_zoom, video_start,
 * video_complete, plating_details_open, size_guide_tab, accordion_toggle,
 * faq_toggle, footer_accordion_toggle, add_to_cart_source, sticky_bar_click,
 * final_sale_info_open, review_link_click, related_tab_change, cart_close,
 * cart_item_click, cart_upsell_toggle, cart_upsell_skip, gift_note_add,
 * continue_shopping_click, large_order_contact, discount_apply,
 * discount_remove, form_error, whatsapp_dismiss, email_click, generate_lead,
 * studio_visit_click, guide_link_click, lookbook_hotspot_click,
 * shop_the_look_click, rewards_join_click, tally_popup, share,
 * cancellation_start, language_switch, cookie_preferences_open,
 * back_to_top_click, footer_link_click, login, sign_up,
 * password_reset_request, order_detail_view, tracking_link_click,
 * address_change. GA4's own parameter names are reused where they exist
 * (link_text, link_url, link_domain, method, percent_scrolled, video_title,
 * coupon) so few custom definitions are needed.
 *
 * Market, store language and page type ride on every GA4 event from the GA4 config
 * in snippets/google-tag.liquid, so they are not repeated here.
 */
(function () {
  'use strict';

  var G = window.DuavaaGoogle;
  if (!G || !G.ga4Id || window.__duavaaGa4InteractionsLoaded) return;
  window.__duavaaGa4InteractionsLoaded = true;
  if (typeof window.gtag !== 'function') return;

  var BRAND = 'Duavaa';

  function readPage() {
    try {
      var el = document.getElementById('duavaa-google-page');
      return (el && JSON.parse(el.textContent)) || {};
    } catch (e) {
      return {};
    }
  }
  var page = readPage();

  function log() {
    if (!G.debug || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[ga4-interactions]');
    window.console.log.apply(window.console, args);
  }

  /** GA4 only. Never the Ads id. */
  function send(name, params) {
    params = params || {};
    params.send_to = G.ga4Id;
    log(name, params);
    try {
      window.gtag('event', name, params);
    } catch (e) {
      // Measurement must never break the page.
    }
  }

  /** True the first time a key is seen within `ms`. */
  var seenAt = {};
  function fresh(key, ms) {
    var now = Date.now();
    if (seenAt[key] && now - seenAt[key] < ms) return false;
    seenAt[key] = now;
    return true;
  }

  // ── Small readers ─────────────────────────────────────────────────────

  function text(el, max) {
    if (!el) return '';
    return String(el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, max || 100);
  }

  /** A link as a path (internal) or host + path (external). Never the query. */
  function linkPath(href) {
    try {
      var u = new URL(href, window.location.href);
      return u.host === window.location.host ? u.pathname : u.host + u.pathname;
    } catch (e) {
      return '';
    }
  }

  function closest(el, selector) {
    return el && typeof el.closest === 'function' ? el.closest(selector) : null;
  }

  // ── Prices and ids, as google-events.js builds them ───────────────────

  function shownPrice(paise) {
    var p = Number(paise) || 0;
    var rate = Number(G.rate) || 1;
    if (rate === 1) return Math.round(p) / 100;
    var rateInt = Math.round(rate * 10000);
    var cents = Math.floor((p * rateInt) / 10000);
    return Math.floor((cents + 99) / 100);
  }

  function offerId(productId, variantId) {
    var label = G.feedLabel || String(G.market || '').toUpperCase();
    return 'shopify_' + label + '_' + productId + '_' + variantId;
  }

  function gaItem(src, index) {
    var out = {
      item_id: offerId(src.product_id, src.variant_id),
      item_name: src.title || '',
      item_brand: BRAND,
      price: shownPrice(src.price),
      quantity: src.quantity || 1
    };
    if (src.variant_title && src.variant_title !== 'Default Title') out.item_variant = src.variant_title;
    if (src.type) out.item_category = src.type;
    if (typeof index === 'number') out.index = index;
    return out;
  }

  function valueOf(items) {
    var total = 0;
    for (var i = 0; i < items.length; i++) total += items[i].price * items[i].quantity;
    return Math.round(total * 100) / 100;
  }

  function fromLine(line) {
    return {
      product_id: line.product_id,
      variant_id: line.variant_id,
      title: line.product_title || line.title,
      variant_title: line.variant_title,
      type: line.product_type,
      price: line.final_price != null ? line.final_price : line.price,
      quantity: line.quantity
    };
  }

  function isServiceLine(line) {
    return !!(line && line.properties && line.properties._Service);
  }

  // ── The product on this page ──────────────────────────────────────────

  var productCache = typeof WeakMap === 'function' ? new WeakMap() : null;

  /** The product JSON nearest to an element (a quick-add modal has its own). */
  function productFor(el) {
    var scope = closest(el, 'dialog, .shopify-section') || document;
    var script = scope.querySelector('script[data-product-json]') || document.querySelector('script[data-product-json]');
    if (!script) return null;
    if (productCache && productCache.has(script)) return productCache.get(script);
    var data = null;
    try {
      data = JSON.parse(script.textContent);
    } catch (e) {
      data = null;
    }
    if (productCache) productCache.set(script, data);
    return data;
  }

  function variantById(product, id) {
    var vs = (product && product.variants) || [];
    for (var i = 0; i < vs.length; i++) if (String(vs[i].id) === String(id)) return vs[i];
    return null;
  }

  /** The variant selected on the product page right now. */
  function currentVariant(el) {
    var product = productFor(el || document.body);
    if (!product) return null;
    var input = document.querySelector('input[name="id"][form^="ProductForm"]');
    var id = input ? input.value : page.items && page.items[0] && page.items[0].variant_id;
    return { product: product, variant: variantById(product, id) || (product.variants || [])[0] || null };
  }

  function currentItemId(el) {
    var cur = currentVariant(el);
    return cur && cur.variant ? offerId(cur.product.id, cur.variant.id) : '';
  }

  // ── Product lists: cards carry data-ga-* (snippets/product-grid-item) ──

  var CARD = '.product-item[data-ga-product-id]';
  var MAIN_LIST = '[data-section-type="collection"]';

  function cardSource(card) {
    return {
      product_id: card.getAttribute('data-ga-product-id'),
      variant_id: card.getAttribute('data-ga-variant-id'),
      title: card.getAttribute('data-ga-title') || '',
      type: card.getAttribute('data-ga-type') || '',
      price: card.getAttribute('data-ga-price')
    };
  }

  /** Which list a card belongs to: the page's own list, or a rail section. */
  function listOf(card) {
    if (closest(card, MAIN_LIST)) {
      if (page.type === 'search') return { id: 'search_results', name: 'Search results', root: closest(card, MAIN_LIST) };
      return { id: page.listId || 'collection', name: page.listName || 'Collection', root: closest(card, MAIN_LIST) };
    }
    var section = closest(card, '[data-section-id]') || closest(card, '.shopify-section');
    if (!section) return { id: 'other', name: 'Other', root: document.body };
    var type = section.getAttribute('data-section-type') || 'section';
    var key = section.getAttribute('data-section-id') || section.id || '';
    var heading = section.querySelector('h1, h2, h3');
    return { id: type + ':' + key, name: text(heading, 80) || type, root: section };
  }

  function cardsIn(root) {
    return Array.prototype.slice.call(root.querySelectorAll(CARD));
  }

  /** A card's position in its own list, from 0, as google-events.js counts. */
  function indexOf(card, list) {
    return cardsIn(list.root).indexOf(card);
  }

  var reportedRails = {};

  function reportRail(section) {
    if (closest(section, MAIN_LIST) || section.matches(MAIN_LIST)) return;
    var cards = cardsIn(section);
    if (!cards.length) return;
    var list = listOf(cards[0]);
    if (reportedRails[list.id]) return;
    reportedRails[list.id] = true;
    var items = [];
    for (var i = 0; i < cards.length && i < 20; i++) items.push(gaItem(cardSource(cards[i]), i));
    send('view_item_list', {
      currency: G.currency,
      item_list_id: list.id,
      item_list_name: list.name,
      list_source: 'rail',
      items: items
    });
  }

  /** Rails count once each, when a third of the section is on screen. */
  function watchRails() {
    if (typeof IntersectionObserver !== 'function') return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var section = entry.target;
          // Related and recently viewed products arrive after load.
          if (!cardsIn(section).length) {
            setTimeout(function () {
              reportRail(section);
            }, 1500);
            return;
          }
          io.unobserve(section);
          reportRail(section);
        });
      },
      { threshold: 0.3 }
    );
    var seen = [];
    function scan() {
      var cards = document.querySelectorAll(CARD);
      for (var i = 0; i < cards.length; i++) {
        var section = closest(cards[i], '[data-section-id]') || closest(cards[i], '.shopify-section');
        if (!section || closest(section, MAIN_LIST) || seen.indexOf(section) !== -1) continue;
        seen.push(section);
        io.observe(section);
      }
      var lazy = document.querySelectorAll('[data-section-type="related"]');
      for (var j = 0; j < lazy.length; j++) {
        if (seen.indexOf(lazy[j]) === -1) {
          seen.push(lazy[j]);
          io.observe(lazy[j]);
        }
      }
    }
    scan();
    document.addEventListener('theme:recently-viewed:loaded', scan);
    window.addEventListener('load', function () {
      setTimeout(scan, 2000);
    });
  }

  /**
   * The page's own list after load: a new grid after a filter or sort, or
   * products added by infinite scroll. google-events.js reports the first
   * screen on load; this reports what arrives later, to GA4 only. Which of the
   * two it is comes from what the shopper just did, not from the products.
   */
  var lastListActionAt = 0;
  function noteListAction() {
    lastListActionAt = Date.now();
  }

  function watchMainList() {
    var root = document.querySelector(MAIN_LIST);
    if (!root || typeof MutationObserver !== 'function') return;
    var reported = {};
    var initial = cardsIn(root);
    for (var i = 0; i < initial.length; i++) reported[initial[i].id] = true;
    var pagesLoaded = 1;
    var handledAction = 0;
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var cards = cardsIn(root);
        if (!cards.length) return;
        var afterAction = lastListActionAt && Date.now() - lastListActionAt < 8000 && handledAction !== lastListActionAt;
        var batch = [];
        var k;
        if (afterAction) {
          // A filter or sort redrew the grid: its first screen is a new list view.
          handledAction = lastListActionAt;
          reported = {};
          pagesLoaded = 1;
          for (k = 0; k < cards.length; k++) reported[cards[k].id] = true;
          batch = cards.slice(0, 24);
        } else {
          for (k = 0; k < cards.length; k++) {
            if (!reported[cards[k].id]) {
              reported[cards[k].id] = true;
              batch.push(cards[k]);
            }
          }
          if (!batch.length) return;
          pagesLoaded++;
        }
        var items = [];
        for (var n = 0; n < batch.length && n < 24; n++) items.push(gaItem(cardSource(batch[n]), cards.indexOf(batch[n])));
        var list = listOf(batch[0]);
        send('view_item_list', {
          currency: G.currency,
          item_list_id: list.id,
          item_list_name: list.name,
          list_source: afterAction ? 'filter_or_sort' : 'load_more',
          page_number: afterAction ? 1 : pagesLoaded,
          items: items
        });
      }, 500);
    }).observe(root, { childList: true, subtree: true });
  }

  // ── Search ─────────────────────────────────────────────────────────────

  /** A term as typed, unless it looks like personal data. */
  function safeTerm(raw) {
    var t = String(raw || '').trim().toLowerCase().slice(0, 100);
    if (/@/.test(t) || /\d{6,}/.test(t.replace(/[\s-]/g, ''))) return '[redacted]';
    return t;
  }

  function searchInput(scope) {
    return (scope && scope.querySelector('input[name="q"]')) || document.querySelector('input[name="q"]');
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var q = form.querySelector('input[name="q"]');
    if (!q || !/\/search/.test(form.getAttribute('action') || '')) return;
    var term = safeTerm(q.value);
    if (!term || !fresh('search:' + term, 3000)) return;
    send('search', { search_term: term, ui_location: closest(form, 'header-search-popdown, .search-popdown') ? 'header' : 'page' });
  }

  function searchNoResultsOnPage() {
    if (page.type !== 'search' || page.resultsCount !== 0 || !page.searchTerm) return;
    send('search_no_results', { search_term: safeTerm(page.searchTerm), ui_location: 'page' });
  }

  /** Predictive search: once per term the shopper stops on with nothing found. */
  function watchPredictive() {
    if (typeof MutationObserver !== 'function') return;
    var els = document.querySelectorAll('predictive-search');
    Array.prototype.forEach.call(els, function (ps) {
      var timer = null;
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (ps.getAttribute('results') !== 'true' || ps.hasAttribute('loading')) return;
          var found = ps.querySelectorAll('[id^="predictive-search-option-"], .predictive-search__grid-item').length;
          var input = searchInput(closest(ps, 'form') || ps);
          var term = safeTerm(input && input.value);
          if (found || term.length < 2 || !fresh('no-results:' + term, 60000)) return;
          send('search_no_results', { search_term: term, ui_location: 'predictive' });
        }, 1200);
      }).observe(ps, { attributes: true, childList: true, subtree: true });
    });
  }

  function onSuggestionClick(a) {
    var ps = closest(a, 'predictive-search');
    if (!ps) return false;
    var input = searchInput(closest(ps, 'form') || ps);
    var m = /^predictive-search-option-([a-z]+)-(\d+)$/.exec(a.id || '');
    var type = m ? m[1] : null;
    var index = m ? Number(m[2]) - 1 : -1;
    if (!type) {
      var item = closest(a, '.predictive-search__grid-item');
      if (!item) return false;
      type = 'product';
      index = Array.prototype.indexOf.call(ps.querySelectorAll('.predictive-search__grid-item'), item);
    }
    send('search_suggestion_click', {
      search_term: safeTerm(input && input.value),
      suggestion_type: type,
      index: index,
      link_url: linkPath(a.href)
    });
    return true;
  }

  // ── Filters and sort ─────────────────────────────────────────────────

  var priceMethod = 'input';
  var priceTimer = null;

  function priceInputs() {
    return {
      min: document.querySelector('input[name="filter.v.price.gte"]'),
      max: document.querySelector('input[name="filter.v.price.lte"]')
    };
  }

  function schedulePrice() {
    noteListAction();
    clearTimeout(priceTimer);
    priceTimer = setTimeout(function () {
      var p = priceInputs();
      var min = p.min && p.min.value !== '' ? Number(p.min.value) : null;
      var max = p.max && p.max.value !== '' ? Number(p.max.value) : null;
      var key = 'price:' + min + ':' + max;
      if (fresh(key, 5000)) {
        send('price_filter_apply', {
          method: priceMethod,
          price_min: min,
          price_max: max,
          currency: G.currency,
          item_list_name: page.listName || (page.type === 'search' ? 'Search results' : '')
        });
      }
      priceMethod = 'input';
    }, 1500);
  }

  function onChange(event) {
    var el = event.target;
    if (!el) return;
    // Facet ticks and the in-stock switch.
    if (el.type === 'checkbox' && /^filter\./.test(el.name || '') && closest(el, 'form[data-collection-filters-form]')) {
      noteListAction();
      send(el.checked ? 'filter_apply' : 'filter_remove', {
        filter_name: el.name,
        filter_value: String(el.value || '').slice(0, 100),
        item_list_name: page.listName || (page.type === 'search' ? 'Search results' : '')
      });
      return;
    }
    if (el.name === 'plating_option') {
      onPlating(el);
    }
  }

  function onInput(event) {
    var el = event.target;
    if (el && (el.name === 'filter.v.price.gte' || el.name === 'filter.v.price.lte')) schedulePrice();
    if (el && el.hasAttribute && el.hasAttribute('data-shipping-input')) lastShippingInputAt = Date.now();
  }

  function onPointerDown(event) {
    var t = event.target;
    if (closest(t, '.filter__price__preset')) priceMethod = 'preset';
    else if (closest(t, 'range-slider')) priceMethod = 'slider';
  }

  function onPointerUp(event) {
    if (closest(event.target, 'range-slider')) schedulePrice();
  }

  // ── Product page ────────────────────────────────────────────────────

  var lastVariantId = null;
  var variantTimer = null;

  function onVariantChange(event) {
    var variant = event && event.detail && event.detail.variant;
    if (!variant || !variant.id) return;
    var target = event.target;
    clearTimeout(variantTimer);
    // A collapsed stack changes several hidden options per click; send the last.
    variantTimer = setTimeout(function () {
      if (String(variant.id) === String(lastVariantId)) return;
      var product = productFor(target) || {};
      var previous = variantById(product, lastVariantId);
      lastVariantId = variant.id;
      var options = product.options || [];
      var changed = 0;
      var opts = variant.options || [];
      for (var i = 0; i < opts.length; i++) {
        if (!previous || (previous.options || [])[i] !== opts[i]) {
          changed = i;
          break;
        }
      }
      send('select_variant', {
        option_name: String(options[changed] && (options[changed].name || options[changed]) || 'option' + (changed + 1)).slice(0, 50),
        option_value: String(opts[changed] || '').slice(0, 100),
        item_id: offerId(product.id || '', variant.id),
        variant_available: variant.available === true,
        currency: G.currency,
        value: shownPrice(variant.price)
      });
    }, 300);
  }

  function onPlating(input) {
    var key = input.getAttribute('data-plating-key') || input.value;
    if (!input.checked || !fresh('plating:' + key, 1000)) return;
    send('plating_select', {
      plating: key === 'silver' ? 'rhodium' : key,
      addon_value: Number(input.getAttribute('data-plating-addon-amount')) || 0,
      currency: G.currency,
      item_id: currentItemId(input)
    });
  }

  // Delivery estimator: reported once the widget shows a result or an error
  // just after the shopper typed or picked something. Never the PIN itself.
  var lastShippingInputAt = 0;

  function watchShipping() {
    if (typeof MutationObserver !== 'function') return;
    var widgets = document.querySelectorAll('[data-shipping-estimate-widget]');
    Array.prototype.forEach.call(widgets, function (widget) {
      widget.addEventListener('change', function (e) {
        if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-shipping-input')) lastShippingInputAt = Date.now();
      });
      var timer = null;
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (Date.now() - lastShippingInputAt > 5000) return;
          var results = widget.querySelector('[data-shipping-results]');
          var error = widget.querySelector('[data-shipping-error]');
          var shown = results && !results.classList.contains('hidden');
          var failed = error && !error.classList.contains('hidden');
          if (!shown && !failed) return;
          var inputType = widget.querySelector('[data-shipping-pin]')
            ? 'pin'
            : widget.querySelector('[data-shipping-emirate]')
            ? 'emirate'
            : widget.querySelector('[data-shipping-zip]')
            ? 'postcode'
            : 'none';
          var result = failed ? 'invalid' : 'shown';
          if (!fresh('shipping:' + result, 3000)) return;
          send('delivery_estimate_check', { input_type: inputType, result: result, item_id: currentItemId(widget) });
        }, 300);
      }).observe(widget, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
    });
  }

  function onAddError(event) {
    var d = (event && event.detail) || {};
    var r = d.response || {};
    var message = String(r.description || r.message || d.description || d.message || 'error').replace(/\s+/g, ' ').slice(0, 100);
    if (!fresh('add-error', 2000)) return;
    send('add_to_cart_error', { error_message: message, item_id: currentItemId(event.target) });
  }

  // ── Cart ──────────────────────────────────────────────────────────────

  var cartSnapshot = null;
  var lastAddAt = 0;
  var drawerOpen = false;

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function refreshSnapshot(delay) {
    setTimeout(function () {
      fetchCart().then(function (cart) {
        if (cart) cartSnapshot = cart;
      });
    }, delay || 0);
  }

  function cartItems(cart) {
    var items = [];
    var lines = (cart && cart.items) || [];
    for (var i = 0; i < lines.length; i++) if (!isServiceLine(lines[i])) items.push(gaItem(fromLine(lines[i])));
    return items;
  }

  function cartTotal(cart) {
    var total = 0;
    var lines = (cart && cart.items) || [];
    for (var i = 0; i < lines.length; i++) {
      var unit = lines[i].final_price != null ? lines[i].final_price : lines[i].price;
      total += shownPrice(unit) * (lines[i].quantity || 1);
    }
    return Math.round(total * 100) / 100;
  }

  function viewCart(trigger) {
    fetchCart().then(function (cart) {
      if (!cart) return;
      cartSnapshot = cart;
      send('view_cart', {
        currency: G.currency,
        value: cartTotal(cart),
        trigger: trigger,
        items: cartItems(cart)
      });
    });
  }

  function lineByKey(key) {
    var lines = (cartSnapshot && cartSnapshot.items) || [];
    for (var i = 0; i < lines.length; i++) if (lines[i].key === key) return lines[i];
    return null;
  }

  function reportQuantity(key, newQty) {
    var line = lineByKey(key);
    if (!line || isServiceLine(line)) return;
    var diff = newQty - (line.quantity || 0);
    if (!diff) return;
    var src = fromLine(line);
    src.quantity = Math.abs(diff);
    var items = [gaItem(src)];
    send(diff < 0 ? 'remove_from_cart' : 'cart_quantity_increase', {
      currency: G.currency,
      value: valueOf(items),
      items: items
    });
    line.quantity = newQty;
    refreshSnapshot(1500);
  }

  function onCartUpdate(event) {
    var d = (event && event.detail) || {};
    if (!d.id) return;
    var qty = parseInt(d.quantity, 10);
    if (isNaN(qty)) return;
    reportQuantity(String(d.id), qty);
  }

  // ── International contact sheet and checkout intent ────────────────────

  var intent = { type: 'unknown', at: 0 };
  var sheet = { open: false, trigger: 'unknown', collected: false };

  function noteIntent(type) {
    intent = { type: type, at: Date.now() };
  }

  function currentTrigger() {
    return Date.now() - intent.at < 30000 ? intent.type : 'unknown';
  }

  function watchContactSheet() {
    var dialog = document.getElementById('contact-details-dialog');
    if (!dialog || typeof MutationObserver !== 'function') return;
    new MutationObserver(function () {
      var isOpen = dialog.hasAttribute('open');
      if (isOpen && !sheet.open) {
        sheet = { open: true, trigger: currentTrigger(), collected: false };
        send('contact_details_open', { trigger: sheet.trigger });
      } else if (!isOpen && sheet.open) {
        closeSheet();
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    dialog.addEventListener('close', closeSheet);
  }

  function closeSheet() {
    if (!sheet.open) return;
    sheet.open = false;
    if (sheet.collected) return;
    var filled = 0;
    ['contact-email', 'contact-phone'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && String(el.value || '').trim()) filled++;
    });
    send('contact_details_abandon', { trigger: sheet.trigger, fields_filled: filled });
  }

  function onContactCollected(event) {
    // The detail holds the email and phone: only the ISO country is read.
    var country = event && event.detail && typeof event.detail.country === 'string' ? event.detail.country.slice(0, 2).toUpperCase() : '';
    var trigger = sheet.open ? sheet.trigger : currentTrigger();
    sheet.collected = true;
    send('add_contact_info', { trigger: trigger, contact_country: country });
    if (trigger === 'made_to_order') {
      send('made_to_order_submit', {
        item_id: lastMadeToOrderItem,
        checkout_flow: G.market === 'in' ? 'india_manual' : 'stripe_advance'
      });
    }
  }

  var lastMadeToOrderItem = '';

  function onMadeToOrder(btn) {
    var productId = btn.getAttribute('data-product-id');
    var variantId = btn.getAttribute('data-variant-id');
    lastMadeToOrderItem = offerId(productId, variantId);
    var lead = null;
    var restock = Date.parse(btn.getAttribute('data-expected-restock-date') || '');
    if (!isNaN(restock)) lead = Math.max(0, Math.round((restock - Date.now()) / 86400000));
    noteIntent('made_to_order');
    send('made_to_order_click', {
      item_id: lastMadeToOrderItem,
      currency: G.currency,
      value: shownPrice(btn.getAttribute('data-variant-price-cents')),
      lead_time_days: lead
    });
  }

  function onBuyNow(btn) {
    var india = btn.hasAttribute('data-buy-now-button-india');
    noteIntent('buy_now');
    var cur = currentVariant(btn);
    send('buy_now_click', {
      checkout_flow: india ? 'razorpay' : 'stripe',
      item_id: cur && cur.variant ? offerId(cur.product.id, cur.variant.id) : '',
      currency: G.currency,
      value: cur && cur.variant ? shownPrice(cur.variant.price) : 0
    });
  }

  // ── WhatsApp ──────────────────────────────────────────────────────────

  var WHATSAPP = 'a[href*="/pages/whatsapp"], a[href*="wa.me"], a[href*="api.whatsapp.com"]';

  /** What the chat is about, from the prefilled message's keywords. Never the message. */
  function whatsappTopic(href) {
    var msg = '';
    try {
      msg = (new URL(href, window.location.href).searchParams.get('text') || '').toLowerCase();
    } catch (e) {
      msg = '';
    }
    if (/urgent|express|fast/.test(msg)) return 'urgent_delivery';
    if (/colou?r/.test(msg)) return 'colour_request';
    if (/stylist|styling/.test(msg)) return 'stylist';
    if (/checkout|payment|pay /.test(msg)) return 'checkout_help';
    if (/items|large|bulk|order of/.test(msg)) return 'large_order';
    return 'general';
  }

  function whatsappLocation(a) {
    if (a.classList.contains('whatsapp-float') || closest(a, '.whatsapp-float')) return 'floating_button';
    if (closest(a, '#duavaa-large-order-modal')) return 'large_order_modal';
    if (closest(a, '.occasion-concierge') || a.classList.contains('occasion-concierge__cta')) return 'concierge';
    if (closest(a, '[data-shipping-estimate-widget]')) return 'delivery_estimate';
    if (page.type === 'product' && closest(a, '.product__block, .product__wrapper, .product-single, product-component')) return 'product';
    return page.type || 'other';
  }

  // ── Clicks: one capture listener on window, so nothing that stops a click
  //    later can hide it, and nothing here can stop it either. ─────────────

  var NAV = 'a.navlink--toplevel, a.navlink--child, a.navlink--grandchild, a.sliderow__title';

  function onClick(event) {
    var t = event.target;
    if (!t || typeof t.closest !== 'function') return;

    var btn = t.closest('[data-buy-now-button], [data-buy-now-button-india]');
    if (btn) return onBuyNow(btn);
    btn = t.closest('[data-preorder-advance-button]');
    if (btn) return onMadeToOrder(btn);
    if (t.closest('[data-tally-checkout-button], button[name="checkout"], .cart__checkout')) {
      noteIntent('cart');
      return;
    }

    var remove = t.closest('a[data-item-remove]');
    if (remove) return reportQuantity(String(remove.getAttribute('data-id') || ''), 0);

    if (t.closest('[data-filter-update-url]')) noteListAction();

    var sort = t.closest('a[data-sort-link]');
    if (sort) {
      noteListAction();
      if (sort.getAttribute('aria-current') !== 'true') {
        send('sort_apply', {
          sort_option: sort.getAttribute('data-value') || '',
          item_list_name: page.listName || (page.type === 'search' ? 'Search results' : '')
        });
      }
      return;
    }

    if (t.closest('[data-cookie-preferences]')) {
      cookiePrefsAt = Date.now();
      send('cookie_preferences_open', {});
      return;
    }

    var a = t.closest('a[href]');
    if (!a) return;

    // The floating button's close (x) sits inside the WhatsApp link: closing
    // is whatsapp_dismiss, not a click to chat.
    if (a.matches(WHATSAPP) && !t.closest('.whatsapp-close-btn')) {
      var wa = { ui_location: whatsappLocation(a), whatsapp_topic: whatsappTopic(a.href) };
      if (page.type === 'product') wa.item_id = currentItemId(a);
      send('whatsapp_click', wa);
      return;
    }

    if (onSuggestionClick(a)) return;

    var card = closest(a, CARD);
    if (card && a.hasAttribute('data-product-link')) {
      var list = listOf(card);
      send('select_item', {
        item_list_id: list.id,
        item_list_name: list.name,
        items: [gaItem(cardSource(card), indexOf(card, list))]
      });
      return;
    }

    if (a.matches(NAV)) {
      var level = a.classList.contains('navlink--toplevel')
        ? 'top'
        : a.classList.contains('navlink--grandchild')
        ? 'grandchild'
        : a.classList.contains('navlink--child')
        ? 'child'
        : 'mobile';
      send('nav_click', {
        menu_level: level,
        link_text: text(a, 100),
        link_url: linkPath(a.href),
        ui_location: closest(a, 'header-drawer') ? 'mobile_drawer' : 'desktop_menu'
      });
      return;
    }

    if (a.classList.contains('sibling__link') && !closest(a, CARD)) {
      send('colour_sibling_click', {
        colour: text(a.querySelector('.visually-hidden'), 60) || (closest(a, 'tooltip-component') || a).getAttribute('data-tooltip') || '',
        from_item_id: currentItemId(a),
        to_url: linkPath(a.href)
      });
      return;
    }

    if (a.hasAttribute('data-popup-open')) {
      var isSizeChart = a.classList.contains('radio__legend__link') || /size/i.test(text(a));
      if (isSizeChart && page.type === 'product') {
        var cur = currentVariant(a);
        send('size_guide_open', {
          item_id: currentItemId(a),
          product_type: (cur && cur.product && cur.product.type) || ''
        });
      }
      return;
    }

    var promo = promotionOf(a);
    if (promo) {
      promo.link_url = linkPath(a.href);
      send('select_promotion', promo);
    }
  }

  /** Hero slides, occasion banners, rich-text buttons and the offer toast. */
  function promotionOf(a) {
    var section;
    if (a.classList.contains('frame__item') && (section = closest(a, '[data-section-type="slideshow"]'))) {
      return promo(section, 'hero_slide', a);
    }
    if (a.classList.contains('occasion-banner__cta') && (section = closest(a, '[data-section-type="occasion-banner"]'))) {
      return promo(section, 'occasion_banner', a);
    }
    if (a.classList.contains('btn') && (section = closest(a, '[data-section-type="rich-text"]'))) {
      return promo(section, 'rich_text', a);
    }
    if (closest(a, '.offer-promo-toast')) {
      return { promotion_id: 'offer_toast', promotion_name: text(a, 100), creative_slot: 'offer_toast' };
    }
    return null;
  }

  function promo(section, slot, a) {
    var heading = section.querySelector('h1, h2, h3');
    return {
      promotion_id: section.getAttribute('data-section-id') || slot,
      promotion_name: text(heading, 100) || a.getAttribute('aria-label') || text(a, 100) || slot,
      creative_slot: slot
    };
  }

  // ── Cookie consent ────────────────────────────────────────────────────

  var cookiePrefsAt = 0;

  /** Once per choice per browser session, in case the API repeats itself. */
  function onConsent(event) {
    var d = (event && event.detail) || {};
    var analytics = d.analyticsAllowed ? 'granted' : 'denied';
    var marketing = d.marketingAllowed ? 'granted' : 'denied';
    var key = 'duavaa_ga4_consent';
    var choice = analytics + '/' + marketing;
    try {
      if (window.sessionStorage.getItem(key) === choice) return;
      window.sessionStorage.setItem(key, choice);
    } catch (e) {
      if (!fresh('consent', 60000)) return;
    }
    send('cookie_consent', {
      analytics: analytics,
      marketing: marketing,
      method: Date.now() - cookiePrefsAt < 120000 ? 'preferences' : 'banner'
    });
  }


  // ════════════════════════════════════════════════════════════════════
  // Phases 2 and 3: how features are used. Same rules as above: GA4 only,
  // listening only, nothing personal.
  // ════════════════════════════════════════════════════════════════════

  function sectionOf(el) {
    return closest(el, '[data-section-id]') || closest(el, '.shopify-section');
  }

  function sectionName(el) {
    var sec = sectionOf(el);
    return sec ? sec.getAttribute('data-section-id') || sec.id || '' : '';
  }

  function scrolledPercent() {
    var doc = document.documentElement;
    var max = doc.scrollHeight - window.innerHeight;
    return max > 0 ? Math.min(100, Math.round((window.scrollY / max) * 100)) : 100;
  }

  function hostOf(href) {
    try {
      return new URL(href, window.location.href).host.replace(/^www\./, '');
    } catch (e) {
      return '';
    }
  }

  function filterCount(search) {
    var n = 0;
    new URLSearchParams(search || window.location.search).forEach(function (v, k) {
      if (/^filter\./.test(k)) n++;
    });
    return n;
  }

  // ── Scroll depth (and articles read) ──────────────────────────────────

  var SCROLL_MARKS = [25, 50, 75, 90];
  var scrollSent = {};
  var scrollQueued = false;

  /** At most one check per 200 ms. A timer, not requestAnimationFrame, which
   *  never runs while a tab is in the background. */
  function onScroll() {
    if (scrollQueued) return;
    scrollQueued = true;
    setTimeout(function () {
      scrollQueued = false;
      var pct = scrolledPercent();
      for (var i = 0; i < SCROLL_MARKS.length; i++) {
        var mark = SCROLL_MARKS[i];
        if (pct < mark || scrollSent[mark]) continue;
        scrollSent[mark] = true;
        send('scroll_depth', { percent_scrolled: mark });
        if (mark === 75 && document.body.classList.contains('template-article')) send('article_read', {});
      }
    }, 200);
  }

  // ── Media gallery and video ───────────────────────────────────────────

  var mediaMethod = 'swipe';
  var mediaSeen = {};
  var maxMediaPosition = 0;

  function reportMedia(root, mediaId, method) {
    if (!mediaId || mediaSeen[mediaId]) return;
    mediaSeen[mediaId] = true;
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-media-id]')).filter(function (el) {
      return !closest(el, '[data-thumb-item], [data-thumb-link]');
    });
    var slide = root.querySelector('[data-media-id="' + mediaId + '"]');
    var position = slides.indexOf(slide) + 1;
    if (position > maxMediaPosition) maxMediaPosition = position;
    send('media_view', {
      media_type: (slide && slide.getAttribute('data-type')) || (/selfvideo/.test(mediaId) ? 'video' : 'image'),
      media_position: position > 0 ? position : null,
      method: method,
      item_id: currentItemId(root)
    });
  }

  function watchGallery() {
    if (typeof MutationObserver !== 'function') return;
    var galleries = document.querySelectorAll('product-images[data-active-media], [data-active-media]');
    Array.prototype.forEach.call(galleries, function (g) {
      var first = g.getAttribute('data-active-media');
      mediaSeen[first] = true;
      new MutationObserver(function () {
        reportMedia(g, g.getAttribute('data-active-media'), mediaMethod);
        mediaMethod = 'swipe';
      }).observe(g, { attributes: true, attributeFilter: ['data-active-media'] });
    });
    // Phones: the gallery is a plain scroll-snap list with no theme event.
    var lists = document.querySelectorAll('[data-product-media-list]');
    Array.prototype.forEach.call(lists, function (list) {
      var timer = null;
      var last = 0;
      list.addEventListener(
        'scroll',
        function () {
          clearTimeout(timer);
          timer = setTimeout(function () {
            if (!list.clientWidth) return;
            var index = Math.round(Math.abs(list.scrollLeft) / list.clientWidth);
            if (index === last) return;
            last = index;
            var slides = list.querySelectorAll('[data-media-id]');
            var slide = slides[index];
            if (slide) reportMedia(list, slide.getAttribute('data-media-id'), 'swipe');
          }, 400);
        },
        { passive: true }
      );
    });
  }

  var videoSeen = {};

  function onVideo(event) {
    var v = event.target;
    if (!v || v.tagName !== 'VIDEO' || !closest(v, '[data-product-media-list], product-images, deferred-media, [data-self-hosted-video], .product__media')) return;
    var src = (v.currentSrc || v.src || '').split('?')[0];
    var key = event.type + ':' + src;
    if (videoSeen[key]) return;
    videoSeen[key] = true;
    send(event.type === 'play' ? 'video_start' : 'video_complete', {
      video_title: src.split('/').pop() || 'product video',
      media_type: 'video',
      item_id: currentItemId(v)
    });
  }

  // ── Accordions, FAQ, footer and cart upsell toggles ───────────────────

  function onCollapsible(event) {
    var details = closest(event.target, 'details');
    if (!details || closest(details, 'form[data-collection-filters-form]')) return;
    var openAttr = details.getAttribute('open');
    var action = openAttr === null || openAttr === 'false' ? 'close' : 'open';
    var summary = details.querySelector('summary');
    var name = text(summary, 80);
    if (details.hasAttribute('data-upsell-widget')) return send('cart_upsell_toggle', { action: action });
    if (closest(details, '[data-section-type="footer"]')) return send('footer_accordion_toggle', { content_name: name, action: action });
    if (closest(details, '[data-section-type="faq"]')) return send('faq_toggle', { content_name: name, action: action });
    var params = { content_name: name, action: action };
    if (page.type === 'product') params.item_id = currentItemId(details);
    send('accordion_toggle', params);
  }

  // ── Promotions in view ────────────────────────────────────────────────

  function watchPromotions() {
    if (typeof IntersectionObserver !== 'function') return;
    var seen = {};
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          io.unobserve(el);
          var slot = el.getAttribute('data-ga-promo-slot');
          var a = el.querySelector('a[href]') || el;
          var p = slot === 'offer_card'
            ? { promotion_id: 'offer_card', promotion_name: text(el, 100), creative_slot: 'offer_card' }
            : promo(el, slot, a);
          if (seen[p.promotion_id]) return;
          seen[p.promotion_id] = true;
          send('view_promotion', p);
        });
      },
      { threshold: 0.5 }
    );
    [
      ['[data-section-type="slideshow"]', 'hero_slide'],
      ['[data-section-type="occasion-banner"]', 'occasion_banner'],
      ['.offer-promo-card', 'offer_card']
    ].forEach(function (pair) {
      Array.prototype.forEach.call(document.querySelectorAll(pair[0]), function (el) {
        el.setAttribute('data-ga-promo-slot', pair[1]);
        io.observe(el);
      });
    });
  }

  // ── Forms: errors, leads, accounts ────────────────────────────────────

  var AUTH_KEY = 'duavaa_ga4_auth';

  function onSubmit2(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = (form.getAttribute('action') || '').split('?')[0];
    try {
      if (/\/account\/login$/.test(action)) window.sessionStorage.setItem(AUTH_KEY, JSON.stringify({ e: 'login', at: Date.now() }));
      else if (form.id === 'create_customer' || /\/account$/.test(action)) window.sessionStorage.setItem(AUTH_KEY, JSON.stringify({ e: 'sign_up', at: Date.now() }));
    } catch (e) {
      // Private mode: the event is simply not sent.
    }
    if (/\/account\/recover$/.test(action)) send('password_reset_request', {});
    if (/\/account\/addresses/.test(action)) {
      send('address_change', { action: form.querySelector('input[name="_method"][value="put"]') ? 'edit' : 'add' });
    }
  }

  /** After a sign-in or sign-up page submit, the next logged-in page reports it once. */
  function authResult() {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(AUTH_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    var flag = null;
    try {
      flag = JSON.parse(raw);
    } catch (e) {
      flag = null;
    }
    if (!flag || Date.now() - flag.at > 10 * 60000) {
      try { window.sessionStorage.removeItem(AUTH_KEY); } catch (e) {}
      return;
    }
    if (!document.body.classList.contains('customer-logged-in')) return;
    try { window.sessionStorage.removeItem(AUTH_KEY); } catch (e) {}
    send(flag.e, { method: 'email' });
  }

  function formsOnLoad() {
    var q = new URLSearchParams(window.location.search);
    var leadKey = 'duavaa_ga4_lead:' + window.location.pathname + window.location.search;
    function once(type) {
      try {
        if (window.sessionStorage.getItem(leadKey)) return;
        window.sessionStorage.setItem(leadKey, '1');
      } catch (e) {
        // fall through: send once per page load
      }
      send('generate_lead', { lead_type: type });
    }
    if (q.get('contact_posted') === 'true') once('contact');
    if (q.get('customer_posted') === 'true') once('newsletter');
    // Errors Shopify rendered into a form after a failed submit.
    Array.prototype.forEach.call(document.querySelectorAll('form'), function (f) {
      // Only Shopify's own error block, which is rendered after a failed submit.
      var errors = f.querySelector('.errors');
      if (!errors || !text(errors)) return;
      var name = f.id || (f.getAttribute('action') || '').split('?')[0].replace(/^\//, '') || 'form';
      send('form_error', { form_name: name.slice(0, 40), field_name: 'unknown', error_type: 'server' });
    });
    if (document.body.classList.contains('template-404')) send('page_not_found', {});
    if (document.body.classList.contains('template-order')) send('order_detail_view', {});
  }

  /** Contact sheet validation messages, by field name only. */
  function watchContactErrors() {
    if (typeof MutationObserver !== 'function') return;
    [['contact-email-error', 'email'], ['contact-phone-error', 'phone']].forEach(function (pair) {
      var el = document.getElementById(pair[0]);
      if (!el) return;
      new MutationObserver(function () {
        var visible = text(el) && el.offsetParent !== null && !el.hidden;
        if (!visible || !fresh('form-error:' + pair[1], 10000)) return;
        send('form_error', { form_name: 'contact_sheet', field_name: pair[1], error_type: 'invalid' });
      }).observe(el, { attributes: true, childList: true, characterData: true, subtree: true });
    });
  }

  // ── Large-order modal and Tally ───────────────────────────────────────

  function watchLargeOrderModal() {
    if (typeof MutationObserver !== 'function') return;
    var shown = false;
    new MutationObserver(function () {
      var modal = document.getElementById('duavaa-large-order-modal');
      if (modal && !shown) {
        shown = true;
        send('large_order_contact', { action: 'shown', items_count: (cartSnapshot && cartSnapshot.item_count) || null });
      } else if (!modal) {
        shown = false;
      }
    }).observe(document.body, { childList: true });
  }

  /** Tally (rewards, India gift popup) posts its events to the page. Never the answers. */
  function onMessage(event) {
    if (!/tally\.so$/.test(hostOf(event.origin || ''))) return;
    var data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return;
      }
    }
    var name = data && data.event;
    var map = { 'Tally.FormLoaded': 'open', 'Tally.FormSubmitted': 'submit', 'Tally.PopupClosed': 'close' };
    if (!map[name]) return;
    var formId = (data.payload && (data.payload.formId || data.payload.id)) || '';
    if (!fresh('tally:' + name + ':' + formId, 3000)) return;
    send('tally_popup', { action: map[name], form_name: String(formId).slice(0, 40) });
  }

  // ── Add-to-cart source ────────────────────────────────────────────────

  var stickyAt = 0;

  function onAddSource(event) {
    var btn = event && event.detail && event.detail.button;
    var where = 'main';
    if (Date.now() - stickyAt < 3000) where = 'sticky_bar';
    else if (btn && closest(btn, 'dialog[data-quick-add-modal]')) where = 'quick_add';
    else if (btn && closest(btn, '.product-upsell, [data-quick-add-holder]')) where = 'upsell';
    else if (btn && closest(btn, 'cart-drawer, [data-cart-form]')) where = 'cart';
    send('add_to_cart_source', { ui_location: where, item_id: currentItemId(btn || document.body) });
  }

  // ── Clicks for phases 2 and 3 (a second capture listener, so the first
  //    one's early returns never hide these) ─────────────────────────────

  function onClick2(event) {
    var t = event.target;
    if (!t || typeof t.closest !== 'function') return;
    var el;

    if ((el = t.closest('[data-thumb-link]'))) mediaMethod = 'thumbnail';
    else if (t.closest('.slider__arrows, [data-button-prev], [data-button-next]') && t.closest('product-images, [data-active-media]')) mediaMethod = 'arrow';

    if ((el = t.closest('[data-zoom-image]'))) {
      var zooms = Array.prototype.slice.call(document.querySelectorAll('[data-zoom-image]'));
      return send('media_zoom', { media_position: zooms.indexOf(el) + 1, item_id: currentItemId(el) });
    }
    if (t.closest('[data-plating-details-open]')) {
      var chosen = document.querySelector('input[name="plating_option"]:checked');
      var key = chosen ? chosen.getAttribute('data-plating-key') : '';
      return send('plating_details_open', { plating: key === 'silver' ? 'rhodium' : key, item_id: currentItemId(t) });
    }
    if ((el = t.closest('li.tab-link'))) {
      if (closest(el, '.product-modal')) return send('size_guide_tab', { content_name: text(el, 60) });
      if (closest(el, '[data-section-type="related"]')) return send('related_tab_change', { content_name: text(el, 60) });
    }
    if (t.closest('[data-add-to-cart-bar]')) {
      stickyAt = Date.now();
      return send('sticky_bar_click', { action: 'add', item_id: currentItemId(t) });
    }
    if (t.closest('[data-cart-bar-scroll]')) return send('sticky_bar_click', { action: 'scroll_to_options', item_id: currentItemId(t) });
    if (t.closest('#cart-bar a[href^="#tally-open"]')) return send('sticky_bar_click', { action: 'made_to_order', item_id: currentItemId(t) });
    if (t.closest('[data-final-sale-badge]')) return send('final_sale_info_open', { item_id: currentItemId(t) });

    if (t.closest('button.header__mobile__hamburger, [data-drawer-toggle="hamburger"]')) return;
    if ((el = t.closest('button[data-sliderule-open]'))) return send('submenu_open', { link_text: text(el, 80), ui_location: 'mobile_drawer' });
    if (t.closest('button.search-reset')) {
      var input = searchInput(closest(t, 'form'));
      return send('search_clear', { search_term: safeTerm(input && input.value) });
    }
    if ((el = t.closest('.popout__toggle--filters'))) {
      if (el.getAttribute('aria-expanded') !== 'true') send('filter_panel_open', { active_filters: filterCount() });
      return;
    }
    if ((el = t.closest('a[data-show-more]'))) {
      var grp = closest(el, 'details') || closest(el, '.sidebar__item') || el.parentNode;
      var first = grp && grp.querySelector ? grp.querySelector('input[name^="filter."]') : null;
      return send('filter_show_more', { filter_name: first ? first.name : '' });
    }
    if ((el = t.closest('a.active__filters__remove'))) {
      var now = new URLSearchParams(window.location.search);
      var next = new URLSearchParams((el.getAttribute('href') || '').split('?')[1] || '');
      var removed = null;
      now.forEach(function (v, k) {
        if (!removed && /^filter\./.test(k) && next.getAll(k).indexOf(v) === -1) removed = { filter_name: k, filter_value: v.slice(0, 100) };
      });
      return send('filter_remove', removed || { filter_name: 'unknown', filter_value: '' });
    }
    if (t.closest('a.active__filters__clear') || (t.closest('[data-filter-update-url]') && !t.closest('a.active__filters__remove'))) {
      return send('filter_clear_all', { active_filters: filterCount() });
    }
    if (t.closest('button[data-skip-upsell-product]')) {
      var holder = closest(t, '[data-quick-add-holder], [data-product-id]');
      return send('cart_upsell_skip', { item_id: holder ? holder.getAttribute('data-product-id') || '' : '' });
    }
    if ((el = t.closest('[data-apply-discount]'))) {
      var field = (closest(el, 'form') || document).querySelector('input[name="discount"], input[name="discount_code"], [data-discount-input]');
      return send('discount_apply', { coupon: String((field && field.value) || '').trim().toUpperCase().slice(0, 30) });
    }
    if ((el = t.closest('[data-remove-discount]'))) {
      var row = closest(el, '[data-discount-code]');
      return send('discount_remove', { coupon: row ? row.getAttribute('data-discount-code') : '' });
    }
    if (t.closest('.whatsapp-close-btn')) return send('whatsapp_dismiss', {});
    if (t.closest('.offer-promo-toast__close')) return send('offer_toast_close', { promotion_id: 'offer_toast' });
    if ((el = t.closest('.duavaa-lom__btn--wa, .duavaa-lom__btn--email, .duavaa-lom__btn--back'))) {
      var act = el.classList.contains('duavaa-lom__btn--wa') ? 'whatsapp' : el.classList.contains('duavaa-lom__btn--email') ? 'email' : 'back';
      return send('large_order_contact', { action: act, items_count: (cartSnapshot && cartSnapshot.item_count) || null });
    }
    if ((el = t.closest('[data-section-type="look"] button'))) {
      var lookName = text((sectionOf(el) || document).querySelector('h1, h2, h3'), 80);
      if (closest(el, '.look__actions')) return send('shop_the_look_click', { content_name: lookName });
      if (el.classList.contains('look__dot__button')) return send('lookbook_hotspot_click', { content_name: lookName });
    }
    if (t.closest('[data-share-button]')) {
      return send('share', { method: navigator.share ? 'native' : 'copy', content_type: page.type === 'product' ? 'product' : 'article' });
    }
    if ((el = t.closest('button[data-scroll-top-button]'))) return send('back_to_top_click', { percent_scrolled: scrolledPercent() });
    if ((el = t.closest('button[name="locale_code"]'))) return send('language_switch', { to_language: el.value || '' });
    if ((el = t.closest('.address-delete'))) return send('address_change', { action: 'delete' });

    var a = t.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href') || '';

    if (a.matches('a[data-language-option]')) return send('language_switch', { to_language: a.getAttribute('hreflang') || '' });
    if (/^mailto:/i.test(href)) {
      return send('email_click', { ui_location: closest(a, '#duavaa-large-order-modal') ? 'large_order_modal' : closest(a, '[data-section-type="footer"], footer') ? 'footer' : page.type || 'page' });
    }
    if (a.matches('a[data-logo-link]')) return send('logo_click', {});
    if (a.classList.contains('social-link')) {
      return send('social_click', { link_domain: hostOf(a.href), ui_location: closest(a, '[data-section-type="footer"], footer') ? 'footer' : closest(a, 'header-drawer') ? 'mobile_drawer' : 'header' });
    }
    if (closest(a, '[data-announcement-wrapper]')) return send('announcement_click', { link_url: linkPath(a.href) });
    if (/\/account(\/|$)/.test(linkPath(a.href)) && closest(a, '.header, header, [data-section-type="header"]')) return send('account_click', { ui_location: 'header' });
    if (a.classList.contains('studio-visit__trigger')) return send('studio_visit_click', { item_id: currentItemId(a) });
    if (/app\.duavaa\.com\/cancel/.test(a.href)) return send('cancellation_start', {});
    if (/^#tally-open=wLQ5ql/.test(href)) return send('rewards_join_click', {});
    if (a.classList.contains('heart-icon')) return send('review_link_click', { item_id: currentItemId(a) });
    if (closest(a, '[data-ga-guide-links]')) return send('guide_link_click', { link_url: linkPath(a.href), link_text: text(a, 60) });
    if (/(^|\s)ai-story-highlight-/.test(a.className || '')) {
      var stories = Array.prototype.slice.call((sectionOf(a) || document).querySelectorAll('a[class*="ai-story-highlight-"]'));
      return send('story_highlight_click', { link_text: text(a, 60), link_url: linkPath(a.href), index: stories.indexOf(a) });
    }
    if (closest(a, '[data-section-type="highlights"], [data-section-type="list-collections"]') || a.classList.contains('collection-item__content')) {
      var tiles = Array.prototype.slice.call((sectionOf(a) || document).querySelectorAll('a[href]'));
      return send('category_tile_click', { link_url: linkPath(a.href), index: tiles.indexOf(a), section_name: sectionName(a) });
    }
    if (a.classList.contains('btn') && closest(a, '[data-section-type="product-grid"]') && !closest(a, CARD)) {
      return send('view_all_click', { link_url: linkPath(a.href), section_name: sectionName(a) });
    }
    if (closest(a, '.cart__item') && !a.hasAttribute('data-item-remove')) {
      var lineEl = closest(a, '[data-item]');
      var line = lineEl ? lineByKey(lineEl.getAttribute('data-item')) : null;
      return send('cart_item_click', { item_id: line ? offerId(line.product_id, line.variant_id) : '', ui_location: closest(a, 'cart-drawer') ? 'cart_drawer' : 'cart_page' });
    }
    if (closest(a, '.drawer__empty__item') || (document.body.classList.contains('template-cart') && a.classList.contains('btn') && /\/collections\//.test(href))) {
      return send('continue_shopping_click', { link_url: linkPath(a.href), cart_state: cartSnapshot && cartSnapshot.item_count ? 'has_items' : 'empty' });
    }
    if (closest(a, 'ul.footer__quicklinks')) return send('footer_link_click', { link_text: text(a, 60), link_url: linkPath(a.href) });
    if (document.body.classList.contains('template-order') && hostOf(a.href) && hostOf(a.href) !== hostOf(window.location.href)) {
      return send('tracking_link_click', { link_domain: hostOf(a.href) });
    }
  }

  /** Card colour swatches: once per card per page view. */
  var swatchSeen = {};
  function onHover(event) {
    var sw = closest(event.target, '[data-sibling-link]');
    if (!sw) return;
    var card = closest(sw, CARD);
    if (!card || swatchSeen[card.id]) return;
    swatchSeen[card.id] = true;
    var tip = closest(sw, '[data-tooltip]') || sw;
    send('colour_swatch_hover', { colour: (tip.getAttribute('data-tooltip') || '').slice(0, 60), item_id: offerId(card.getAttribute('data-ga-product-id'), card.getAttribute('data-ga-variant-id')) });
  }

  function onChange2(event) {
    var el = event.target;
    if (el && el.name === 'attributes[Gift note]' && String(el.value || '').trim()) {
      if (fresh('gift-note', 30000)) send('gift_note_add', {});
    }
  }

  function onToggle(event) {
    var d = event.target;
    if (d && d.tagName === 'DETAILS' && d.open && d.querySelector(':scope > summary.navlink--search')) send('search_open', { ui_location: 'header' });
  }

  function onKeyDown(event) {
    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && closest(event.target, 'product-images, [data-active-media]')) mediaMethod = 'keyboard';
  }

  // ── Wiring ────────────────────────────────────────────────────────────

  window.addEventListener('click', onClick, true);
  window.addEventListener('popstate', noteListAction);
  window.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
  window.addEventListener('pointerup', onPointerUp, { capture: true, passive: true });
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('change', onChange, true);
  document.addEventListener('input', onInput, true);
  document.addEventListener('theme:variant:change', onVariantChange);
  document.addEventListener('theme:product:add-error', onAddError);
  document.addEventListener('theme:cart:error', onAddError);
  document.addEventListener('theme:cart:update', onCartUpdate);
  document.addEventListener('contactDetailsCollected', onContactCollected);
  document.addEventListener('visitorConsentCollected', onConsent);

  // Listening only: google-events.js reports the add itself.
  document.addEventListener('theme:product:add', function () {
    lastAddAt = Date.now();
    refreshSnapshot(1200);
  });
  document.addEventListener('theme:cart-drawer:open', function () {
    if (drawerOpen) return;
    drawerOpen = true;
    viewCart(Date.now() - lastAddAt < 4000 ? 'after_add' : 'icon');
  });
  document.addEventListener('theme:cart-drawer:close', function () {
    drawerOpen = false;
    send('cart_close', {
      items_count: (cartSnapshot && cartSnapshot.item_count) || 0,
      currency: G.currency,
      value: cartSnapshot ? cartTotal(cartSnapshot) : 0
    });
  });

  // Phases 2 and 3.
  window.addEventListener('click', onClick2, true);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('keydown', onKeyDown, { capture: true, passive: true });
  window.addEventListener('message', onMessage);
  document.addEventListener('mouseover', onHover, { passive: true });
  document.addEventListener('submit', onSubmit2, true);
  document.addEventListener('change', onChange2, true);
  document.addEventListener('toggle', onToggle, true);
  document.addEventListener('play', onVideo, true);
  document.addEventListener('ended', onVideo, true);
  document.addEventListener('theme:collapsible:toggle', onCollapsible);
  document.addEventListener('theme:product:add', onAddSource);
  document.addEventListener('theme:quick-add:open', function () {
    send('quick_add_open', {});
  });
  document.addEventListener('theme:drawer:open', function (e) {
    if (closest(e.target, 'header-drawer') || (e.target && e.target.tagName === 'HEADER-DRAWER')) send('menu_open', { ui_location: 'mobile_drawer' });
  });

  function start() {
    var cur = page.type === 'product' ? currentVariant(document.body) : null;
    lastVariantId = cur && cur.variant ? cur.variant.id : null;
    searchNoResultsOnPage();
    watchRails();
    watchMainList();
    watchPredictive();
    watchShipping();
    watchContactSheet();
    if (document.body && document.body.classList.contains('template-cart')) viewCart('page');
    watchGallery();
    watchPromotions();
    watchContactErrors();
    watchLargeOrderModal();
    formsOnLoad();
    authResult();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
